import {
  navigation,
  secondaryNavigation,
} from '@/components/layouts/sidebar/sidebarLinks';
import {
  CONTACTS_SUBNAV,
  GROUPS_SUBNAV,
  SETTINGS_SUBNAV,
} from '@/lib/navRegistry';
import { FEATURES } from '@/access/features';

/**
 * @typedef {{
 *   hasPermission: (key: string) => boolean,
 *   isFeatureEnabled: (key: string) => boolean,
 *   isVectorRecruiting: boolean,
 *   userRole: string | null | undefined,
 *   hasStandaloneObjects: boolean,
 *   isAdmin: boolean,
 *   currentUser: { role?: string } | null,
 * }} NavTreeAuthContext
 */

/**
 * @typedef {{ label: string, path?: string, routeName?: string, children?: NavTreeNode[] }} NavTreeNode
 */

/**
 * Primary sidebar visibility — the single definition; do not re-implement it in AppSidebar (see agentPermissionGates.spec.js).
 *
 * @param {object} link
 * @param {NavTreeAuthContext} ctx
 */
export function filterPrimaryLink(link, ctx) {
  const { permission, permissions, feature, roles = [] } = link;

  if (permission && !ctx.hasPermission(permission)) {
    return false;
  }

  // OR form — any one of the listed permissions grants access (tiered
  // features such as Agents: view/edit/manage). Mirrors filterSecondaryLink.
  if (permissions?.length && !permissions.some((p) => ctx.hasPermission(p))) {
    return false;
  }

  // Feature gating is deliberately narrow: only Vector Recruiting orgs hide
  // links for disabled features generally, but custom_agents hides for
  // everyone (agents are not shown to orgs that don't have the feature).
  if (
    feature &&
    !ctx.isFeatureEnabled(feature) &&
    (ctx.isVectorRecruiting || feature === FEATURES.CUSTOM_AGENTS)
  ) {
    return false;
  }

  if (roles.length && !roles.includes(ctx.userRole)) {
    return false;
  }

  return true;
}

/**
 * Secondary / "More" — matches SecondaryNav.
 * @param {object} link
 * @param {NavTreeAuthContext} ctx
 */
function filterSecondaryLink(link, ctx) {
  const {
    permission,
    permissions,
    roles = [],
    feature,
    requiresStandaloneObjects,
  } = link;

  if (permission && !ctx.hasPermission(permission)) {
    return false;
  }

  if (permissions?.length) {
    const hasAny = permissions.some((p) => ctx.hasPermission(p));
    if (!hasAny) {
      return false;
    }
  }

  if (feature && !ctx.isFeatureEnabled(feature)) {
    return false;
  }

  if (roles?.length && !roles.includes(ctx.userRole)) {
    return false;
  }

  if (requiresStandaloneObjects && !ctx.hasStandaloneObjects) {
    return false;
  }

  return true;
}

/**
 * Contacts sub-nav — matches Contacts.vue `filterLinks`.
 * @param {typeof CONTACTS_SUBNAV[number]} link
 * @param {NavTreeAuthContext} ctx
 */
function filterContactsChild(link, ctx) {
  if (link.feature && !ctx.isFeatureEnabled(link.feature)) {
    return false;
  }
  return true;
}

/**
 * Settings sub-nav — matches Settings.vue `filterLinks`.
 * @param {typeof SETTINGS_SUBNAV[number]} link
 * @param {NavTreeAuthContext} ctx
 */
function filterSettingsChild(link, ctx) {
  if (!ctx.currentUser) {
    return false;
  }
  if (link.feature && !ctx.isFeatureEnabled(link.feature)) {
    return false;
  }
  if (link.permission && !ctx.hasPermission(link.permission)) {
    if (!ctx.isAdmin) {
      return false;
    }
  }
  if (
    link.permissions?.length &&
    !link.permissions.some((p) => ctx.hasPermission(p))
  ) {
    if (!ctx.isAdmin) {
      return false;
    }
  }
  if (link.hidden) {
    return false;
  }
  if (link.roles?.length && !link.roles.includes(ctx.currentUser.role)) {
    return false;
  }
  return true;
}

/**
 * Groups sub-nav — feature `groups` on each entry.
 * @param {typeof GROUPS_SUBNAV[number]} link
 * @param {NavTreeAuthContext} ctx
 */
function filterGroupsChild(link, ctx) {
  if (link.feature && !ctx.isFeatureEnabled(link.feature)) {
    return false;
  }
  return true;
}

/**
 * Map a flat link to a tree node (no children).
 * @param {{ text: string, path: string }} link
 * @returns {NavTreeNode}
 */
function toNode(link) {
  return {
    label: link.text,
    path: link.path,
  };
}

/**
 * Build a filtered navigation tree for agent / help linkification.
 *
 * @param {NavTreeAuthContext} ctx
 * @returns {{ tree: NavTreeNode[], visibleNavLabels: string[] }}
 */
export function buildAppNavTree(ctx) {
  /** @type {NavTreeNode[]} */
  const tree = [];
  /** @type {string[]} */
  const visibleNavLabels = [];

  const primaryFiltered = navigation.filter((link) =>
    filterPrimaryLink(link, ctx)
  );

  for (const link of primaryFiltered) {
    /** @type {NavTreeNode} */
    const node = {
      label: link.text,
      path: link.path,
    };

    if (link.path === '/contacts') {
      const children = CONTACTS_SUBNAV.filter((c) =>
        filterContactsChild(c, ctx)
      ).map((c) => ({
        label: c.label,
        path: c.path,
        routeName: c.routeName,
      }));
      if (children.length) {
        node.children = children;
      }
    }

    if (link.path === '/groups') {
      const children = GROUPS_SUBNAV.filter((c) =>
        filterGroupsChild(c, ctx)
      ).map((c) => ({
        label: c.label,
        path: c.path,
        routeName: c.routeName,
      }));
      if (children.length) {
        node.children = children;
      }
    }

    tree.push(node);
    visibleNavLabels.push(link.text);
  }

  const secondaryFiltered = secondaryNavigation.filter((link) =>
    filterSecondaryLink(link, ctx)
  );

  for (const link of secondaryFiltered) {
    tree.push(toNode(link));
    visibleNavLabels.push(link.text);
  }

  tree.push({
    label: 'Settings',
    path: '/settings',
    children: SETTINGS_SUBNAV.filter((c) => filterSettingsChild(c, ctx)).map(
      (c) => ({
        label: c.label,
        path: c.path,
        routeName: c.routeName,
      })
    ),
  });
  visibleNavLabels.push('Settings');

  return { tree, visibleNavLabels };
}
